import { Component, type ErrorInfo, type ReactNode } from "react";
import styles from "./ErrorBoundary.module.css";

interface Props {
	children: ReactNode;
	fallback?: ReactNode;
}

interface State {
	hasError: boolean;
	error: Error | null;
}

const CHUNK_RELOAD_KEY = "chunk_reload_attempted";

/**
 * Error Boundary component that catches React rendering errors.
 * Provides graceful error recovery, especially for stale chunk loading failures.
 */
export class ErrorBoundary extends Component<Props, State> {
	constructor(props: Props) {
		super(props);
		this.state = { hasError: false, error: null };
	}

	static getDerivedStateFromError(error: Error): State {
		return { hasError: true, error };
	}

	componentDidCatch(error: Error, errorInfo: ErrorInfo) {
		console.error("🔴 ErrorBoundary caught error:", error);
		console.error("Component stack:", errorInfo.componentStack);

		// Check if this is a chunk loading error
		const errorMessage = error.message || "";
		const isChunkError =
			errorMessage.includes("MIME type") ||
			errorMessage.includes("dynamically imported module") ||
			errorMessage.includes("Loading chunk") ||
			errorMessage.includes("Unexpected token '<'");

		// If it's a chunk error and we haven't already tried reloading, reload
		const hasJustReloaded = sessionStorage.getItem(CHUNK_RELOAD_KEY) === "true";
		if (isChunkError && !hasJustReloaded) {
			console.log("🔄 Chunk error detected in ErrorBoundary, reloading...");
			sessionStorage.setItem(CHUNK_RELOAD_KEY, "true");
			window.location.reload();
		}
	}

	handleReload = () => {
		// Clear the reload flag and reload
		sessionStorage.removeItem(CHUNK_RELOAD_KEY);
		window.location.reload();
	};

	render() {
		if (this.state.hasError) {
			// Allow custom fallback
			if (this.props.fallback) {
				return this.props.fallback;
			}

			// Default error UI
			return (
				<div className={styles.container}>
					<div className={styles.content}>
						<div className={styles.icon}>⚠️</div>
						<h2 className={styles.title}>Something went wrong</h2>
						<p className={styles.message}>
							The app encountered an unexpected error. Please try reloading.
						</p>
						<button
							type="button"
							onClick={this.handleReload}
							className={styles.button}
						>
							Reload App
						</button>
					</div>
				</div>
			);
		}

		return this.props.children;
	}
}
